import React from 'react';
import { motion } from 'framer-motion';
import { ArrowLeft, Building, Mail, Phone, MapPin, FileText, Shield } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import Logo from '../components/Logo';
import { legalInfo, associationInfo } from '../mock';

const LegalNotice = () => {
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-orange-50 to-amber-50">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-white/80 backdrop-blur-xl border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/">
            <Logo />
          </Link>
          <Link to="/">
            <Button variant="outline" className="flex items-center gap-2">
              <ArrowLeft className="w-4 h-4" />
              Retour à l'accueil
            </Button>
          </Link>
        </div>
      </nav>

      {/* Content */}
      <div className="pt-32 pb-20 px-6">
        <div className="max-w-4xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <div className="text-center mb-12">
              <Shield className="w-16 h-16 mx-auto mb-6 text-orange-600" />
              <h1 className="text-5xl font-bold mb-4 bg-gradient-to-br from-slate-900 to-orange-800 bg-clip-text text-transparent">
                Mentions Légales
              </h1>
              <p className="text-slate-600">
                Informations légales relatives au site {associationInfo.name}
              </p>
            </div>

            <div className="space-y-6">
              {/* Identité de l'association */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-orange-700">
                    <Building className="w-5 h-5" />
                    Identité de l'Association
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-3 text-slate-700">
                  <div>
                    <span className="font-semibold">Nom de l'association :</span> {legalInfo.associationName}
                  </div>
                  <div>
                    <span className="font-semibold">Forme juridique :</span> {legalInfo.formeJuridique}
                  </div>
                  <div>
                    <span className="font-semibold">Date de création :</span> {legalInfo.dateCreation}
                  </div>
                  <div>
                    <span className="font-semibold">SIRET :</span> {legalInfo.siret}
                  </div>
                  <div>
                    <span className="font-semibold">RNA :</span> {legalInfo.rna}
                  </div>
                  <div className="text-sm text-slate-500 italic pt-2">
                    Le répertoire INSEE est actuellement en cours de mise à jour.
                  </div>
                </CardContent>
              </Card>

              {/* Siège social */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-orange-700">
                    <MapPin className="w-5 h-5" />
                    Siège Social
                  </CardTitle>
                </CardHeader>
                <CardContent className="text-slate-700">
                  <p>{legalInfo.siegeSocial}</p>
                </CardContent>
              </Card>

              {/* Objet de l'association */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-orange-700">
                    <FileText className="w-5 h-5" />
                    Objet de l'Association
                  </CardTitle>
                </CardHeader>
                <CardContent className="text-slate-700">
                  <p>{legalInfo.objet}</p>
                </CardContent>
              </Card>

              {/* Directeur de publication */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-orange-700">
                    <Mail className="w-5 h-5" />
                    Directeur de Publication
                  </CardTitle>
                </CardHeader>
                <CardContent className="text-slate-700">
                  <p>{legalInfo.directeurPublication}</p>
                </CardContent>
              </Card>

              {/* Contact */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-orange-700">
                    <Phone className="w-5 h-5" />
                    Contact
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-2 text-slate-700">
                  <div className="flex items-center gap-2">
                    <Mail className="w-4 h-4 text-orange-600" />
                    <a href="/contact" className="text-orange-700 hover:text-orange-600 underline" data-testid="legal-contact-link">
                      Formulaire de contact
                    </a>
                  </div>
                  <div className="flex items-center gap-2">
                    <MapPin className="w-4 h-4 text-orange-600" />
                    <span>{associationInfo.address}, {associationInfo.city}</span>
                  </div>
                </CardContent>
              </Card>

              {/* Hébergeur */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-orange-700">
                    <Building className="w-5 h-5" />
                    Hébergement
                  </CardTitle>
                </CardHeader>
                <CardContent className="text-slate-700">
                  <p className="font-semibold">{legalInfo.hebergeur.nom}</p>
                  <p className="text-sm text-slate-600">{legalInfo.hebergeur.adresse}</p>
                </CardContent>
              </Card>

              {/* Propriété intellectuelle */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-orange-700">
                    <Shield className="w-5 h-5" />
                    Propriété Intellectuelle
                  </CardTitle>
                </CardHeader>
                <CardContent className="text-slate-700">
                  <p>
                    L'ensemble du contenu de ce site (textes, images, vidéos) est la propriété de {legalInfo.sigle} 
                    ou fait l'objet d'une autorisation d'utilisation. Toute reproduction, même partielle, 
                    est interdite sans autorisation préalable.
                  </p>
                </CardContent>
              </Card>

              {/* Protection des données */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-orange-700">
                    <Shield className="w-5 h-5" />
                    Protection des Données Personnelles
                  </CardTitle>
                </CardHeader>
                <CardContent className="text-slate-700">
                  <p>Conformément au Règlement Général sur la Protection des Données (RGPD), vous disposez d'un droit d'accès, de rectification et de suppression de vos données personnelles. Pour exercer ce droit, contactez-nous via notre <a href="/contact" className="text-orange-700 hover:text-orange-600 underline">formulaire de contact</a>.</p>
                </CardContent>
              </Card>
            </div>

            <div className="mt-12 text-center">
              <Link to="/">
                <Button size="lg" className="bg-gradient-to-r from-orange-600 to-amber-600 text-white">
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  Retour à l'accueil
                </Button>
              </Link>
            </div>
          </motion.div>
        </div>
      </div>
    </div>
  );
};

export default LegalNotice;
